// src/pages/users/UserForm.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Box,
  MenuItem,
  Grid,
  Typography,
  FormHelperText,
  Card,
  CardContent,
  Checkbox,
} from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import userService from '../../services/userService';
import LayoutContainer from '../../components/LayoutContainer';
import AdminHeader from '../../components/AdminHeader';
import { filter, find, includes, map, omit, toUpper } from 'lodash';
import locationService from '../../services/locationService';
import companyService from '../../services/companyService';
import roleService from '../../services/roleService';
import departmentService from '../../services/departmentService';
import permissionService from '../../services/permissionService';
import functionalDepartmentService from '../../services/functionalDepartmentService';

const FunctionalDepartmentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    name: '',
    event_id: localStorage?.getItem('event_id'),
    reporting_head: '',
    vendor_id: '',
  });
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    // const fetchLocations = async () => {
    //   const response = await locationService.getLocations();
    //   setLocations(response?.data);
    // };

    // const fetchDepartment = async () => {
    //   const response = await departmentService.getDepartments();
    //   setDepartments(response.data);
    // };
    // fetchDepartment();

    const fetchUsers = async () => {
      const response = await userService?.getUsers();

      const userList = filter(
        map(response?.data, (data) => {
          return {
            ...data,
            roles: data?.roles ? JSON.parse(data?.roles) : [],
          };
        }),
        (data) => includes(data?.roles, 'REPORTING HEAD')
      );
      setUsers(userList);
    };

    const fetchVendors = async () => {
      const response = await companyService?.getCompanies();
      setVendors(response?.data);
    };

    const fetchvendorById = async () => {
      const response = await functionalDepartmentService?.getFunctionalDepartment(id);
      setInitialValues(response?.data);
    };

    fetchUsers();
    fetchVendors();
    if (id) {
      fetchvendorById();
    }
  }, [id]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Department name is required'),
    vendor_id: Yup.string().required('Vendor is required'),
    reporting_head: Yup.string().required('Reporting head is required'),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (id) {
        await functionalDepartmentService.updateFunctionalDepartment(id, values);
      } else {
        await functionalDepartmentService.createFunctionalDepartment(values);
      }
      navigate('/functionalDepartments');
    } catch (error) {
      console.error(error); // Handle error appropriately
    } finally {
      setSubmitting(false);
    }
  };

  const menuBox = {
    PaperProps: {
      style: {
        maxHeight: 200,
        width: 160,
      },
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    getContentAnchorEl: null,
  };

  return (
    <LayoutContainer>
      <AdminHeader
        title={id ? 'Edit Department' : 'Add Department'}
        backTo='/functionalDepartments'
      />
      <Box
        className='page-content-wrapper scrollable'
        p={3}
        mb={3}
        sx={{ display: 'flex', backgroundColor: '#efefef' }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={!!id}
        >
          {({
            handleChange,
            handleBlur,
            handleSubmit,
            values,
            errors,
            touched,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit} style={{ width: '50%' }}>
              <Card>
                <CardContent>
                  <Grid container spacing={3} sx={{ p: 1 }}>
                    <Grid item xs={12}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Department Name<span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        name='name'
                        fullWidth
                        value={values?.name}
                        onChange={(e) => {
                          const capitalizedValue = toUpper(e.target.value); // Capitalize first letter
                          handleChange({
                            target: {
                              name: 'name',
                              value: capitalizedValue,
                            },
                          });
                        }}
                        onBlur={handleBlur}
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched?.name && errors?.name
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                      />
                      {touched?.name && errors?.name && (
                        <FormHelperText error>{errors?.name}</FormHelperText>
                      )}
                    </Grid>
                    <Grid item xs={12} sx={{ pt: '10px !important' }}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Reporting Head <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        select
                        name='reporting_head'
                        fullWidth
                        value={values.reporting_head || ''}
                        onChange={(e) => {
                          setFieldValue('reporting_head', e?.target?.value);
                        }}
                        sx={{
                          borderRadius: 1,
                          backgroundColor: '#f5f5f5',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched['reporting_head'] &&
                                errors['reporting_head']
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                        onBlur={handleBlur}
                        SelectProps={{
                          MenuProps: {
                            ...menuBox,
                          },
                        }}
                      >
                        {users?.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.username}
                          </MenuItem>
                        ))}
                      </TextField>
                      {touched?.reporting_head && errors?.reporting_head && (
                        <FormHelperText error>
                          {errors?.reporting_head}
                        </FormHelperText>
                      )}
                    </Grid>
                    <Grid item xs={12} sx={{ pt: '10px !important' }}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Vendor <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        select
                        name='vendor_id'
                        fullWidth
                        value={values.vendor_id}
                        onChange={(e) => {
                          setFieldValue('vendor_id', e?.target?.value);
                        }}
                        SelectProps={{
                          MenuProps: {
                            ...menuBox,
                          },
                        }}
                        onBlur={handleBlur}
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.vendor_id && errors.vendor_id
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                      >
                        {vendors?.map((vendor) => (
                          <MenuItem key={vendor.id} value={vendor.id}>
                            {vendor.name}
                          </MenuItem>
                        ))}
                      </TextField>
                      {touched.vendor_id && errors.vendor_id && (
                        <FormHelperText error>
                          {errors.vendor_id}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      type='submit'
                      variant='contained'
                      sx={{ backgroundColor: '#be210b', textTransform: 'none' }}
                    >
                      {id ? 'Update' : 'Create'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Form>
          )}
        </Formik>
      </Box>
    </LayoutContainer>
  );
};

export default FunctionalDepartmentForm;
